import React, { useEffect, useRef, useState } from "react";
import {
  fromClockTimeValue,
  formatClockTimeParts,
  isValidClockTimeInput,
  normalizeClockTimeInput,
  parseHmsParts,
  toClockTimeValue,
} from "../../utils/samplingCpueUtils";

const HOUR_12_OPTIONS = Array.from({ length: 12 }, (_, i) => i + 1);
const MIN_OPTIONS = Array.from({ length: 60 }, (_, i) => i);

const pad2 = (n) => String(n).padStart(2, "0");

function toPickerState(parts) {
  const hour24 = parts?.h ?? 0;
  const period = hour24 >= 12 ? "PM" : "AM";
  let hour12 = hour24 % 12;
  if (hour12 === 0) hour12 = 12;
  return { hour12, minute: parts?.m ?? 0, period };
}

function fromPickerState(hour12, minute, period) {
  let h = Number(hour12) % 12;
  if (period === "PM") h += 12;
  return { h, m: Number(minute) || 0, s: 0 };
}

/**
 * Digits-only typing → auto-insert ":" and optional AM/PM.
 * Examples: "930" → "9:30", "1200p" → "12:00 PM", "845a" → "8:45 AM"
 */
export function autoFormatClockTyping(raw) {
  const upper = String(raw || "").toUpperCase();
  let period = null;
  if (/(PM|P)\s*$/.test(upper) && !/AM\s*$/.test(upper)) period = "PM";
  else if (/(AM|A)\s*$/.test(upper)) period = "AM";

  const digits = upper.replace(/\D/g, "").slice(0, 4);
  if (!digits) return period || "";

  let timePart = "";
  if (digits.length <= 2) {
    timePart = String(parseInt(digits, 10));
  } else if (digits.length === 3) {
    timePart = `${parseInt(digits[0], 10)}:${digits.slice(1)}`;
  } else {
    let hour = parseInt(digits.slice(0, 2), 10);
    const min = digits.slice(2);
    if (hour > 12) {
      // Treat as 1-digit hour + 3 leftover was wrong; use first digit as hour if > 12
      hour = parseInt(digits[0], 10);
      timePart = `${hour}:${digits.slice(1, 3)}`;
    } else {
      timePart = `${hour}:${min}`;
    }
  }

  return period ? `${timePart} ${period}` : timePart;
}

/**
 * Clock time of day: pick from dropdowns or type digits (colon/AM-PM inserted for you).
 */
const ClockTimeInput = ({
  id,
  name,
  value,
  onChange,
  className = "",
  style,
  placeholder = "1200 AM",
  disabled,
  onBlur: onBlurProp,
  onFocus: onFocusProp,
  ...rest
}) => {
  const rootRef = useRef(null);
  const [localValue, setLocalValue] = useState("");
  const [focused, setFocused] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickHour12, setPickHour12] = useState(12);
  const [pickMinute, setPickMinute] = useState(0);
  const [pickPeriod, setPickPeriod] = useState("AM");

  useEffect(() => {
    if (!focused) {
      setLocalValue(value ? toClockTimeValue(value) : "");
    }
  }, [value, focused]);

  useEffect(() => {
    if (!pickerOpen) return;
    const parts = parseHmsParts(value || localValue) || { h: 0, m: 0, s: 0 };
    const next = toPickerState(parts);
    setPickHour12(next.hour12);
    setPickMinute(next.minute);
    setPickPeriod(next.period);
  }, [pickerOpen, value, localValue]);

  useEffect(() => {
    if (!pickerOpen) return;
    const close = (e) => {
      if (!rootRef.current?.contains(e.target)) {
        setPickerOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [pickerOpen]);

  const emitChange = (nextValue) => {
    onChange?.({
      target: {
        id: id || name,
        name: name || id,
        value: nextValue,
      },
    });
  };

  const applyPickedTime = (hour12, minute, period) => {
    const parts = fromPickerState(hour12, minute, period);
    const display = formatClockTimeParts(parts);
    const stored = fromClockTimeValue(display);
    emitChange(stored);
    setLocalValue(display);
  };

  const handlePickerChange = (field, raw) => {
    const hour12 = field === "h" ? Number(raw) : pickHour12;
    const minute = field === "m" ? Number(raw) : pickMinute;
    const period = field === "p" ? raw : pickPeriod;
    setPickHour12(hour12);
    setPickMinute(minute);
    setPickPeriod(period);
    applyPickedTime(hour12, minute, period);
  };

  const handleChange = (e) => {
    const formatted = autoFormatClockTyping(e.target.value);
    setLocalValue(formatted);

    // Live-commit when we have a full parseable clock time (with or without period — default AM if hour/min complete)
    const withPeriod =
      /\b(AM|PM)\b/i.test(formatted)
        ? formatted
        : (() => {
            const digits = formatted.replace(/\D/g, "");
            if (digits.length < 3) return null;
            return `${formatted} AM`;
          })();

    if (withPeriod && isValidClockTimeInput(normalizeClockTimeInput(withPeriod))) {
      const stored = fromClockTimeValue(normalizeClockTimeInput(withPeriod));
      emitChange(stored);
    }
  };

  const handleKeyDown = (e) => {
    // Block colon and other punctuation — we insert ":" automatically
    if (e.key === ":" || e.key === ";" || e.key === ".") {
      e.preventDefault();
    }
  };

  const handleFocus = (e) => {
    setFocused(true);
    onFocusProp?.(e);
  };

  const handleBlur = (e) => {
    if (rootRef.current?.contains(e.relatedTarget)) {
      return;
    }
    setFocused(false);
    let raw = e.target.value.trim();
    if (!raw) {
      emitChange("");
      setLocalValue("");
      onBlurProp?.(e);
      return;
    }

    // If user typed digits only, assume AM when period missing
    if (!/\b(AM|PM)\b/i.test(raw) && /\d/.test(raw)) {
      raw = `${raw} AM`;
    }

    const normalized = normalizeClockTimeInput(raw);
    if (isValidClockTimeInput(normalized)) {
      const stored = fromClockTimeValue(normalized);
      emitChange(stored);
      setLocalValue(toClockTimeValue(stored));
    } else {
      emitChange(raw);
      setLocalValue(autoFormatClockTyping(raw));
    }
    onBlurProp?.(e);
  };

  const togglePicker = () => {
    if (disabled) return;
    setPickerOpen((open) => !open);
  };

  const pickerSelectStyle = {
    width: "4.25rem",
    fontSize: "0.85rem",
    padding: "0.25rem 0.35rem",
    borderRadius: "6px",
    border: "1px solid var(--wf-input-border, #dee2e6)",
    backgroundColor: "var(--wf-input-bg, #fff)",
    color: "var(--wf-input-text, inherit)",
  };

  return (
    <div
      ref={rootRef}
      className={`clock-time-input position-relative ${className}`.trim()}
      style={style}
    >
      <input
        type="text"
        inputMode="numeric"
        autoComplete="off"
        id={id}
        name={name}
        className="clock-time-input__field"
        style={{
          width: "100%",
          height: "100%",
          border: "none",
          outline: "none",
          background: "transparent",
          color: "inherit",
          paddingRight: "2.25rem",
          fontSize: "inherit",
        }}
        value={localValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        title="Pick time or type digits (e.g. 930p)"
        {...rest}
      />
      <button
        type="button"
        className="clock-time-input__trigger btn btn-link p-0 border-0 position-absolute top-50 end-0 translate-middle-y me-2"
        onClick={togglePicker}
        disabled={disabled}
        tabIndex={-1}
        aria-label="Open time picker"
        aria-expanded={pickerOpen}
        style={{
          color: "var(--wf-text-muted, #6c757d)",
          lineHeight: 1,
          textDecoration: "none",
        }}
      >
        <i className="fa fa-clock" aria-hidden />
      </button>

      {pickerOpen && (
        <div
          className="clock-time-input__picker shadow-sm rounded-2 p-2 position-absolute end-0"
          style={{
            top: "calc(100% + 4px)",
            zIndex: 1080,
            backgroundColor: "var(--wf-input-bg, #fff)",
            border: "1px solid var(--wf-input-border, #dee2e6)",
            minWidth: "14.5rem",
          }}
          role="dialog"
          aria-label="Time picker"
        >
          <div className="d-flex gap-2 justify-content-center align-items-end">
            <div className="text-center">
              <div className="small text-muted mb-1">Hour</div>
              <select
                aria-label="Hour"
                value={pickHour12}
                onChange={(e) => handlePickerChange("h", e.target.value)}
                style={pickerSelectStyle}
              >
                {HOUR_12_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
            <div className="text-center">
              <div className="small text-muted mb-1">Min</div>
              <select
                aria-label="Minutes"
                value={pickMinute}
                onChange={(e) => handlePickerChange("m", e.target.value)}
                style={pickerSelectStyle}
              >
                {MIN_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {pad2(n)}
                  </option>
                ))}
              </select>
            </div>
            <div className="text-center">
              <div className="small text-muted mb-1">AM/PM</div>
              <select
                aria-label="AM or PM"
                value={pickPeriod}
                onChange={(e) => handlePickerChange("p", e.target.value)}
                style={pickerSelectStyle}
              >
                <option value="AM">AM</option>
                <option value="PM">PM</option>
              </select>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClockTimeInput;
